/**
 * AuthManager - Handles Auth0 authentication flow with callback handler service
 * Phase 3: SDK Auth Manager Implementation
 *
 * This replaces the @auth0/auth0-react approach with a custom implementation
 * that uses our Cloud Run callback handler service to work with wildcard domains.
 */

export interface AuthConfig {
  auth0Domain: string; // e.g., "pantheon-development.us.auth0.com"
  auth0ClientId: string; // Dashboard client ID
  callbackUrl: string; // Cloud Run callback handler URL
  siteId: string; // Current site UUID
}

interface CookieOptions {
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  maxAge?: number;
}

interface MASTokenResponse {
  token: string;
  expiresIn: number;
}

/**
 * Internal user profile returned from the API after registration
 */
interface RegisteredUser {
  id: string;  // Internal UUID
}

/**
 * Registration function type - called after login to sync user profile
 */
type RegisterUserFn = (profile: UserProfile) => Promise<RegisteredUser>;

/**
 * UserProfile - Decoded from the id_token JWT
 * Contains user information for display purposes (name, avatar, etc.)
 */
export interface UserProfile {
  sub: string; // User ID (Auth0 user_id)
  name: string; // Full name
  given_name: string; // First name
  family_name: string; // Last name
  email: string; // Email address
  picture: string; // Avatar URL
}

/**
 * AuthManager - Manages authentication state and token exchange
 *
 * Flow:
 * 1. User clicks login -> redirect to auth-callback /authorize endpoint
 * 2. Auth-callback creates encrypted state and redirects to Auth0
 * 3. Auth0 redirects to callback handler with code
 * 4. Callback handler exchanges code for token
 * 5. Callback handler redirects back with token in URL fragment
 * 6. SDK detects fragment, sets cookie, exchanges for MAS token
 */
export class AuthManager {
  private config: AuthConfig;
  private masToken: string | null = null;
  private masTokenExpiry: number = 0;
  private currentUser: UserProfile | null = null;
  private internalUserId: string | null = null;
  private idToken: string | null = null;  // Store id_token for API requests (hackweek - TODO: upgrade to MAS token)
  private exchangeTokenFn: (accessToken: string, siteId: string) => Promise<MASTokenResponse>;
  private registerUserFn?: RegisterUserFn;

  constructor(
    config: AuthConfig,
    exchangeTokenFn: (accessToken: string, siteId: string) => Promise<MASTokenResponse>,
    registerUserFn?: RegisterUserFn
  ) {
    this.config = config;
    this.exchangeTokenFn = exchangeTokenFn;
    this.registerUserFn = registerUserFn;

    // Restore idToken from cookie if available (persisted across page refreshes)
    const storedIdToken = this.getCookie('X-Pantheon-ID-Token');
    if (storedIdToken) {
      this.idToken = storedIdToken;
      this.currentUser = this.decodeIdToken(storedIdToken);
    }

    // NOTE: handleCallback() is called by AuthManagerContext, not here, to avoid double-calling
  }

  /**
   * Check if user is authenticated (has access token cookie)
   */
  isAuthenticated(): boolean {
    const cookie = this.getCookie('X-Pantheon-Access-Token');
    return !!cookie && cookie.length > 0;
  }

  /**
   * Initiate Auth0 login flow
   * Redirects to auth-callback /authorize endpoint which handles state encryption
   */
  login(): void {
    // Store any existing hash fragment (like comment deep links) before auth redirect
    // This prevents double-hash issues: #collab-hub-comment=...#access_token=...
    const currentHash = window.location.hash;
    if (currentHash && !currentHash.includes('access_token=')) {
      sessionStorage.setItem('pantheon-auth-pre-login-hash', currentHash);
    }

    // Build auth-callback authorize URL
    // The auth-callback service handles state encryption and redirects to Auth0
    const authorizeUrl = new URL(
      this.config.callbackUrl.replace('/callback', '/authorize')
    );
    // Pass only base URL without hash to avoid double-hash on return
    const returnUrl = window.location.origin + window.location.pathname + window.location.search;
    authorizeUrl.searchParams.set('returnTo', returnUrl);

    // Redirect to auth-callback (same tab)
    window.location.href = authorizeUrl.toString();
  }

  /**
   * Handle auth callback (check for tokens in URL fragment)
   * Called automatically on construction and can be called manually
   *
   * The callback handler redirects with: #access_token=...&id_token=...
   * - access_token: Used for MAS token exchange (stored in cookie)
   * - id_token: JWT containing user profile claims (decoded for display)
   */
  async handleCallback(): Promise<void> {
    const hash = window.location.hash;

    if (!hash.includes('access_token=')) {
      return;
    }

    // Parse fragment
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const idToken = params.get('id_token');

    if (!accessToken) {
      return;
    }

    // Decode id_token if present to get user profile
    if (idToken) {
      this.idToken = idToken;  // Store in memory for API requests (hackweek - TODO: upgrade to MAS token)
      this.currentUser = this.decodeIdToken(idToken);

      // Also store id_token in cookie for persistence across page refreshes
      const hostname = window.location.hostname;
      let idTokenCookieDomain: string | undefined;
      if (hostname.endsWith('.pantheonsite.io') || hostname.endsWith('.pantheon.io')) {
        idTokenCookieDomain = '.pantheon.io';
      } else if (hostname.endsWith('.pantheon-preview.app')) {
        idTokenCookieDomain = '.pantheon-preview.app';
      }
      this.setCookie('X-Pantheon-ID-Token', idToken, {
        domain: idTokenCookieDomain,
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'None',
        maxAge: 3600, // 1 hour (same as access token)
      });

      // Register user in the backend (upsert) if registration function provided
      if (this.currentUser && this.registerUserFn) {
        try {
          const registeredUser = await this.registerUserFn(this.currentUser);
          this.internalUserId = registeredUser.id;
        } catch (error) {
          // Log but don't block the auth flow - user can still be registered later
          console.error('Failed to register user:', error);
        }
      }
    }

    // Set cookie - use appropriate domain for pantheon sites, otherwise use current domain
    const hostname = window.location.hostname;
    let cookieDomain: string | undefined;
    if (hostname.endsWith('.pantheonsite.io') || hostname.endsWith('.pantheon.io')) {
      cookieDomain = '.pantheon.io';
    } else if (hostname.endsWith('.pantheon-preview.app')) {
      cookieDomain = '.pantheon-preview.app';
    }
    this.setCookie('X-Pantheon-Access-Token', accessToken, {
      domain: cookieDomain,
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'None',
      maxAge: 3600, // 1 hour
    });

    // Restore any pre-login hash (like comment deep links) and clear auth fragment
    const preLoginHash = sessionStorage.getItem('pantheon-auth-pre-login-hash');
    sessionStorage.removeItem('pantheon-auth-pre-login-hash');

    // Build new URL: base path + any pre-login hash (for comment deep links)
    const newUrl = window.location.pathname + window.location.search + (preLoginHash || '');
    window.history.replaceState(null, '', newUrl);

    // Dispatch auth success event
    window.dispatchEvent(new CustomEvent('pantheon-auth-success'));
  }

  /**
   * Decode an id_token JWT to extract user profile claims
   * Note: This is for display purposes only - no signature verification
   */
  private decodeIdToken(idToken: string): UserProfile | null {
    try {
      const parts = idToken.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format: expected 3 parts, got', parts.length);
        return null;
      }

      // Decode the payload (middle part)
      const payload = parts[1];
      const decoded = JSON.parse(atob(payload));

      return {
        sub: decoded.sub || '',
        name: decoded.name || '',
        given_name: decoded.given_name || '',
        family_name: decoded.family_name || '',
        email: decoded.email || '',
        picture: decoded.picture || '',
      };
    } catch (error) {
      console.warn('Failed to decode id_token:', error);
      return null;
    }
  }

  /**
   * Get the current user profile (decoded from id_token)
   * Returns null if not authenticated or id_token was not available
   */
  getCurrentUser(): UserProfile | null {
    return this.currentUser;
  }

  /**
   * Get the internal user UUID (from backend registration)
   * Returns null if user hasn't been registered yet
   */
  getInternalUserId(): string | null {
    return this.internalUserId;
  }

  /**
   * Get the id_token for API requests
   * Used in hackweek/standalone mode to identify the user
   * TODO: Upgrade to MAS token exchange for production
   */
  getIdToken(): string | null {
    return this.idToken;
  }

  /**
   * Logout user and clear auth state
   * Redirects to callback service logout endpoint which then redirects to Auth0 logout
   */
  logout(): void {
    // Clear cookies
    this.deleteCookie('X-Pantheon-Access-Token');
    this.deleteCookie('X-Pantheon-ID-Token');

    // Clear cached MAS token and user data
    this.masToken = null;
    this.masTokenExpiry = 0;
    this.currentUser = null;
    this.internalUserId = null;
    this.idToken = null;

    // Redirect to logout endpoint with return URL
    const logoutUrl = new URL(this.config.callbackUrl.replace('/callback', '/logout'));
    logoutUrl.searchParams.set('returnTo', window.location.href);

    window.location.href = logoutUrl.toString();
  }

  /**
   * Get MAS token for API requests
   * Exchanges access token cookie for site-scoped MAS token with caching
   */
  async getMASToken(): Promise<string> {
    // Check cache (with 2s buffer for expiration)
    if (this.masToken && Date.now() < this.masTokenExpiry - 2000) {
      return this.masToken;
    }

    // Get access token from cookie
    const accessToken = this.getCookie('X-Pantheon-Access-Token');
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    // Exchange for MAS token
    try {
      const response = await this.exchangeTokenFn(accessToken, this.config.siteId);
      this.masToken = response.token;
      // Set expiry with buffer (expiresIn is in seconds, convert to ms and subtract 2s)
      this.masTokenExpiry = Date.now() + (response.expiresIn - 2) * 1000;

      return this.masToken;
    } catch (error) {
      console.error('MAS token exchange failed:', error);
      throw new Error('MAS token exchange failed');
    }
  }

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    try {
      const cookieStr = document.cookie || '';
      const cookies = cookieStr.split(';');
      for (const cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === name) {
          return value || null;
        }
      }
      return null;
    } catch (error) {
      // In test environments, cookie jar can become corrupted
      return null;
    }
  }

  /**
   * Set cookie with options
   */
  private setCookie(name: string, value: string, options: CookieOptions): void {
    let cookie = `${name}=${value}`;

    const isTest = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

    // Skip domain in test environments (happy-dom doesn't support it)
    if (options.domain && !isTest) {
      cookie += `; domain=${options.domain}`;
    }
    if (options.path) cookie += `; path=${options.path}`;
    // Skip secure and samesite in test environments (happy-dom has limited support)
    if (options.secure && !isTest) cookie += '; secure';
    if (options.sameSite && !isTest) cookie += `; samesite=${options.sameSite}`;
    if (options.maxAge) cookie += `; max-age=${options.maxAge}`;

    document.cookie = cookie;
  }

  /**
   * Delete cookie by setting past expiry
   */
  private deleteCookie(name: string): void {
    // Skip domain in test environments (happy-dom doesn't support it)
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } else {
      // Determine the appropriate domain for the cookie
      const hostname = window.location.hostname;
      let domainStr = '';
      if (hostname.endsWith('.pantheonsite.io') || hostname.endsWith('.pantheon.io')) {
        domainStr = '; domain=.pantheon.io';
      } else if (hostname.endsWith('.pantheon-preview.app')) {
        domainStr = '; domain=.pantheon-preview.app';
      }
      document.cookie = `${name}=${domainStr}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

}
